import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { 
  Mail, 
  Lock, 
  ArrowRight, 
  Heart, 
  Shield, 
  Eye, 
  EyeOff,
  CheckCircle,
  Sparkles,
  Brain
} from 'lucide-react'

import { useAuth } from '../../hooks/useAuth'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Card } from '../../components/ui/Card'
import { ensureDemoUserExists } from '../../lib/demo-setup'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

export const Login: React.FC = () => {
  const { t } = useTranslation()
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [demoLoading, setDemoLoading] = useState(false)

  const from = location.state?.from?.pathname || '/dashboard'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true)
    try {
      const { error } = await signIn(data.email, data.password)
      
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          toast.error('Invalid email or password. Please check your credentials.')
        } else if (error.message.includes('Email not confirmed')) {
          toast.error('Please check your email and confirm your account before signing in.')
        } else {
          toast.error('Failed to sign in. Please try again.')
        }
      } else {
        toast.success('Welcome back! 🎉')
        navigate(from, { replace: true })
      }
    } catch (error: any) {
      console.error('Login error:', error)
      toast.error('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleDemoAccount = async () => {
    setDemoLoading(true)
    try {
      toast.loading('Setting up demo account...', { id: 'demo-setup' })
      
      // Ensure demo user exists
      const result = await ensureDemoUserExists()
      
      if (result.exists) {
        // Set the form values
        setValue('email', 'demo@mentra.com')
        setValue('password', 'demo123456')
        
        toast.dismiss('demo-setup')
        
        if (result.created) {
          toast.success('Demo account created! Signing you in...', { duration: 2000 })
        } else {
          toast.success('Demo credentials loaded! Signing you in...', { duration: 2000 })
        }
        
        // Auto-submit the form
        setTimeout(async () => {
          const { error } = await signIn('demo@mentra.com', 'demo123456')
          
          if (error) {
            toast.error('Failed to sign in with demo account. Please try again.')
          } else {
            toast.success('Welcome to the Mentra demo! 🎉')
            navigate(from, { replace: true })
          }
        }, 1000)
      } else {
        toast.dismiss('demo-setup')
        toast.error('Failed to set up demo account. Please try again or contact support.')
      }
    } catch (error) {
      console.error('Demo setup error:', error)
      toast.dismiss('demo-setup')
      toast.error('Failed to set up demo account. Please try again.')
    } finally {
      setDemoLoading(false)
    }
  }

  const benefits = [
    { icon: Shield, text: "HIPAA-Compliant Security" },
    { icon: Heart, text: "Empathetic AI Insights" },
    { icon: CheckCircle, text: "Trusted by 1000+ Teams" },
    { icon: Sparkles, text: "Easy Setup & Use" }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-lavender-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Welcome Back */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden lg:block"
        >
          <div className="text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center justify-center lg:justify-start mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-sage-500 to-emerald-600 rounded-2xl flex items-center justify-center mr-4">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-sage-800">Mentra</h1>
              </div>
              
              <h2 className="text-4xl font-bold text-sage-900 mb-4">
                Welcome Back to Your
                <span className="text-sage-600 block">Wellness Dashboard</span>
              </h2>
              
              <p className="text-xl text-sage-600 mb-8 leading-relaxed">
                Continue supporting your team's mental health with AI-powered insights and compassionate care.
              </p>
            </motion.div>

            {/* Mental Health Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="mb-8"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.pexels.com/photos/3760263/pexels-photo-3760263.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop"
                  alt="Team wellness and collaboration"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-sage-600/20 to-emerald-600/20" />
                
                {/* Floating wellness indicator */}
                <motion.div
                  className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl p-3 shadow-lg"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="text-xs font-medium text-sage-800">Team Wellness Active</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="space-y-4 mb-8"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.text}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="flex items-center"
                >
                  <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center mr-4">
                    <benefit.icon className="w-5 h-5 text-sage-600" />
                  </div>
                  <span className="text-sage-700 font-medium">{benefit.text}</span>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className="bg-gradient-to-r from-sage-500 to-emerald-600 rounded-2xl p-6 text-white"
            >
              <h3 className="font-semibold mb-2">Your Team's Wellbeing Matters</h3>
              <p className="text-sage-100 text-sm">
                Every check-in, every insight, every moment of care contributes to a healthier, happier workplace.
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* Right Side - Login Form */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md mx-auto lg:mx-0"
        >
          {/* Mobile Header */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-sage-500 to-emerald-600 rounded-xl flex items-center justify-center mr-3">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-sage-800">Mentra</h1>
            </div>
            <p className="text-sage-600">Welcome back to your wellness dashboard</p>
          </div>

          <Card className="p-8 shadow-2xl border-2 border-sage-100 bg-white/90 backdrop-blur-sm">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-sage-800 mb-2">
                Sign In to Your Account
              </h2>
              <p className="text-sage-600 text-sm">
                Access your team's wellness dashboard and insights
              </p>
              <p className="text-sage-600 text-sm mt-2">
                Don't have an account?{' '}
                <Link
                  to="/auth/register"
                  className="text-sage-600 hover:text-sage-700 font-medium underline"
                >
                  Create one here
                </Link>
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sage-400 w-5 h-5" />
                <Input
                  label="Email Address"
                  type="email"
                  {...register('email')}
                  error={errors.email?.message}
                  placeholder="demo@mentra.com"
                  className="pl-11 border-sage-200 focus:border-sage-500 focus:ring-sage-500/20"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sage-400 w-5 h-5" />
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password')}
                  error={errors.password?.message}
                  placeholder="••••••••"
                  className="pl-11 pr-11 border-sage-200 focus:border-sage-500 focus:ring-sage-500/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sage-400 hover:text-sage-600"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-sage-300 text-sage-600 focus:ring-sage-500"
                  />
                  <span className="ml-2 text-sm text-sage-600">Remember me</span>
                </label>
                <Link
                  to="/auth/forgot-password"
                  className="text-sm text-sage-600 hover:text-sage-700 underline"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                loading={loading}
                className="w-full bg-gradient-to-r from-sage-500 to-emerald-600 hover:from-sage-600 hover:to-emerald-700"
                size="lg"
              >
                {loading ? 'Signing In...' : 'Sign In'}
                {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </form>

            {/* Demo Account */}
            <div className="mt-6 p-4 bg-sage-50 border border-sage-200 rounded-lg">
              <h4 className="text-sm font-medium text-sage-800 mb-2">
                🚀 Try Demo Account
              </h4>
              <p className="text-xs text-sage-700 mb-3">
                Explore Mentra with our demo account (automatically created)
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full text-sage-700 border-sage-300 hover:bg-sage-100"
                onClick={handleDemoAccount}
                loading={demoLoading}
              >
                {demoLoading ? 'Setting up demo...' : 'Use Demo Account'}
              </Button>
            </div>

            {/* Terms */}
            <div className="mt-6 text-center">
              <p className="text-xs text-sage-500">
                By signing in, you agree to our{' '}
                <a href="#" className="text-sage-600 hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-sage-600 hover:underline">Privacy Policy</a>
              </p>
            </div>
          </Card>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-6 flex justify-center space-x-6 text-sage-500"
          >
            <div className="flex items-center text-xs">
              <Shield className="w-4 h-4 mr-1" />
              <span>HIPAA Compliant</span>
            </div>
            <div className="flex items-center text-xs">
              <Lock className="w-4 h-4 mr-1" />
              <span>256-bit SSL</span>
            </div>
            <div className="flex items-center text-xs">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>SOC 2 Certified</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}